import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Bell, CheckCircle2, Clock, ListChecks, Loader2, Play, X,
  Trash2, AlarmClock, ChevronRight, RefreshCw, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { api, errorMessage, API_BASE, tokenStore, orgStore } from "@/lib/api";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { ASSISTANT_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";

const MODULES = [
  { key: "leads", label: "Leads" },
  { key: "sequences", label: "Sequences" },
  { key: "agents", label: "Agents" },
  { key: "content", label: "Content" },
  { key: "kb", label: "Knowledge base" },
];

export default function Assistant() {
  const [notifications, setNotifications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [openNotif, setOpenNotif] = useState(null);
  const [tab, setTab] = useState("inbox");
  const tip = useSamTip("assistant");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, t] = await Promise.all([
        api.get("/assistant/notifications"),
        api.get("/assistant/tasks"),
      ]);
      setNotifications(n.data);
      setTasks(t.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (running) return;
    setRunning(true); setProgress("Pulling activity…");
    try {
      const token = tokenStore.get();
      const orgId = orgStore.get();
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (orgId) headers["X-Org-Id"] = orgId;
      const res = await fetch(`${API_BASE}/assistant/run`, { method: "POST", headers });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.type === "progress") setProgress(evt.message);
          if (evt.type === "done") setProgress(`Done — ${evt.tasks_created} new tasks`);
        }
      }
      toast.success("Briefing ready");
      await load();
    } catch (err) { toast.error(errorMessage(err)); }
    finally {
      setRunning(false);
      setTimeout(() => setProgress(""), 2500);
    }
  };

  const dismissNotif = async (id) => {
    try { await api.post(`/assistant/notifications/${id}/dismiss`); load(); setOpenNotif(null); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const readNotif = async (n) => {
    setOpenNotif(n);
    if (n.status === "unread") {
      try { await api.post(`/assistant/notifications/${n.id}/read`); load(); } catch (_) {}
    }
  };

  const completeTask = async (id) => {
    try { await api.post(`/assistant/tasks/${id}/complete`); load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const snoozeTask = async (id, hours = 24, label = "1 day") => {
    try {
      await api.post(`/assistant/tasks/${id}/snooze`, { snooze_hours: hours });
      load();
      toast(`Snoozed ${label}`);
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const dismissTask = async (id) => {
    try { await api.post(`/assistant/tasks/${id}/dismiss`); load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const activeNotifs = notifications.filter((n) => n.status !== "dismissed");
  const pendingTasks = tasks.filter((t) => t.status === "pending");
  const completedTasks = tasks.filter((t) => t.status === "completed");

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-eyebrow text-indigo-400" data-testid="assistant-eyebrow">Executive assistant</div>
            <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
              Your <span className="brand-text">briefings</span>, on autopilot.
            </h1>
            <p className="mt-2 max-w-xl text-white/55">
              The assistant watches your org daily, distils what matters, and queues the
              tasks worth doing. Click "Run now" to refresh.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/app/assistant/settings">
              <Button variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="ea-settings-link">Settings</Button>
            </Link>
            <Button onClick={runNow} disabled={running} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="ea-run-now">
              {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}
              Run now
            </Button>
          </div>
        </div>
        {progress && <div className="mt-3 text-xs text-fuchsia-300" data-testid="ea-progress">{progress}</div>}
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="ea-tabs">
          <TabsTrigger value="inbox" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="ea-tab-inbox">
            <Bell className="mr-1 h-3.5 w-3.5" /> Inbox
          </TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="ea-tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-3">
      <SamTipCard
        show={!loading && notifications.length === 0 && tasks.length === 0}
        enabled={tip.enabled}
        message={tip.message}
        prefill={tip.prefill}
        testId="sam-tip-assistant"
        module="assistant"
      />
      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        <div className="glass rounded-2xl p-5" data-testid="ea-feed">
          <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-fuchsia-300" />
            <h2 className="font-display text-xl font-light">Notification feed</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : activeNotifs.length === 0 ? (
            <EmptyState icon={Bell} title="No briefings yet" body='Click "Run now" to generate your first.' />
          ) : (
            <div className="mt-4 space-y-2">
              <AnimatePresence>
                {activeNotifs.map((n) => (
                  <motion.button
                    key={n.id} layout
                    onClick={() => readNotif(n)}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition ${n.status === "unread" ? "border-fuchsia-500/30 bg-fuchsia-500/5" : "border-white/5 bg-white/[0.02]"} hover:bg-white/[0.06]`}
                    data-testid={`ea-notif-${n.id}`}
                  >
                    <div className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${n.status === "unread" ? "bg-fuchsia-400" : "bg-white/20"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-white">{n.title}</div>
                      <div className="line-clamp-1 text-xs text-white/45">{(n.body_md || "").replace(/[#*_-]/g, " ").slice(0, 120)}</div>
                    </div>
                    <div className="text-right text-[10px] text-white/40">{new Date(n.created_at).toLocaleString()}</div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-5" data-testid="ea-tasks">
          <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-emerald-300" />
            <h2 className="font-display text-xl font-light">Tasks</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : pendingTasks.length === 0 ? (
            <EmptyState icon={ListChecks} title="Inbox zero." body="No follow-ups queued. Nice." />
          ) : (
            <ul className="mt-4 space-y-2">
              {pendingTasks.map((t) => (
                <li key={t.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid={`ea-task-${t.id}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => completeTask(t.id)} className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border border-white/30 hover:border-emerald-400 hover:bg-emerald-500/10" aria-label="Complete task" data-testid={`ea-task-complete-${t.id}`}>
                      <CheckCircle2 className="h-3 w-3 text-emerald-300 opacity-0 hover:opacity-100" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white">{t.title}</div>
                      {t.description && <div className="mt-0.5 text-xs text-white/45">{t.description}</div>}
                      {t.source_ref?.type && (
                        <Link to={sourceHref(t.source_ref)} className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fuchsia-300 hover:underline">
                          Open {t.source_ref.type} <ChevronRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="rounded p-1 text-white/45 hover:bg-white/5 hover:text-white"
                            title="Snooze"
                            data-testid={`ea-task-snooze-${t.id}`}
                          >
                            <AlarmClock className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40 border-white/10 bg-[#0a0a0a] text-white">
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/40">
                            Snooze for
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => snoozeTask(t.id, 1, "1 hour")}
                            data-testid={`ea-task-snooze-1h-${t.id}`}
                          >
                            <Clock className="mr-2 h-3.5 w-3.5 text-white/55" />
                            1 hour
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => snoozeTask(t.id, 24, "1 day")}
                            data-testid={`ea-task-snooze-1d-${t.id}`}
                          >
                            <AlarmClock className="mr-2 h-3.5 w-3.5 text-white/55" />
                            1 day
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => snoozeTask(t.id, 168, "1 week")}
                            data-testid={`ea-task-snooze-1w-${t.id}`}
                          >
                            <AlarmClock className="mr-2 h-3.5 w-3.5 text-fuchsia-300" />
                            1 week
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <button onClick={() => dismissTask(t.id)} className="rounded p-1 text-white/45 hover:bg-red-500/10 hover:text-red-300" title="Dismiss" data-testid={`ea-task-dismiss-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {completedTasks.length > 0 && (
            <div className="mt-5 border-t border-white/5 pt-3">
              <div className="text-eyebrow text-white/40">Completed · {completedTasks.length}</div>
              <ul className="mt-2 space-y-1">
                {completedTasks.slice(0, 5).map((t) => (
                  <li key={t.id} className="flex items-center gap-2 text-xs text-white/40 line-through">
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" /> {t.title}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
        </TabsContent>
        <TabsContent value="guide" className="mt-3">
          <ModuleGuide
            title={ASSISTANT_GUIDE.title}
            whatItIs={ASSISTANT_GUIDE.whatItIs}
            capabilities={ASSISTANT_GUIDE.capabilities}
            howTo={ASSISTANT_GUIDE.howTo}
            ctaLabel={ASSISTANT_GUIDE.cta}
            onCta={() => setTab("inbox")}
          />
        </TabsContent>
      </Tabs>

      <Sheet open={!!openNotif} onOpenChange={(o) => !o && setOpenNotif(null)}>
        <SheetContent side="right" className="w-full max-w-xl overflow-y-auto border-white/10 bg-[#0a0a0a] text-white" data-testid="ea-notif-drawer">
          {openNotif && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl">{openNotif.title}</SheetTitle>
                <SheetDescription className="text-white/55">{new Date(openNotif.created_at).toLocaleString()}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 whitespace-pre-wrap rounded-xl border border-white/5 bg-black/40 p-4 text-sm text-white/85">{openNotif.body_md}</div>
              <div className="mt-4 flex justify-end">
                <Button onClick={() => dismissNotif(openNotif.id)} variant="ghost" className="text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="ea-notif-dismiss">
                  <X className="mr-1 h-3.5 w-3.5" /> Dismiss
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function sourceHref(ref) {
  if (!ref) return "#";
  if (ref.type === "lead") return ref.id ? `/app/leads?lead=${ref.id}` : "/app/leads";
  if (ref.type === "sequence") return ref.id ? `/app/sequences/${ref.id}` : "/app/sequences";
  if (ref.type === "agent") return ref.id ? `/app/agents/${ref.id}` : "/app/agents";
  if (ref.type === "content") return "/app/content";
  if (ref.type === "kb") return "/app/knowledge";
  return "#";
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center text-white/55">
      <Icon className="mx-auto h-7 w-7 text-white/30" />
      <div className="font-display mt-3 text-lg font-light">{title}</div>
      <p className="mt-1 text-xs">{body}</p>
    </div>
  );
}

// ============ SETTINGS ============

export function AssistantSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/assistant/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errorMessage(e))).finally(() => setLoading(false));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/assistant/config", patch);
      setCfg(r.data);
      toast.success("Saved");
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="flex items-center justify-center py-20 text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const toggleModule = (m) => {
    const next = cfg.monitored_modules.includes(m)
      ? cfg.monitored_modules.filter((x) => x !== m)
      : [...cfg.monitored_modules, m];
    save({ monitored_modules: next });
  };

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/app/assistant" className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white" data-testid="assistant-back">
        ← Back to assistant
      </Link>
      <h1 className="font-display mt-4 text-3xl font-light">Assistant settings</h1>

      <div className="mt-6 glass rounded-2xl p-5">
        <Label className="text-xs uppercase tracking-wider text-white/50">Run schedule</Label>
        <Select value={String(cfg.run_interval_hours)} onValueChange={(v) => save({ run_interval_hours: parseInt(v, 10) })}>
          <SelectTrigger className="mt-2 border-white/10 bg-black/40 text-white" data-testid="ea-schedule"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
            <SelectItem value="6">Every 6 hours</SelectItem>
            <SelectItem value="12">Every 12 hours</SelectItem>
            <SelectItem value="24">Every 24 hours (daily)</SelectItem>
            <SelectItem value="168">Weekly</SelectItem>
          </SelectContent>
        </Select>
        <p className="mt-2 text-xs text-white/40">Last run: {cfg.last_run_at ? new Date(cfg.last_run_at).toLocaleString() : "—"} · {cfg.cycle_count} cycles</p>
      </div>

      <div className="mt-4 glass rounded-2xl p-5" data-testid="ea-monitor-grid">
        <Label className="text-xs uppercase tracking-wider text-white/50">Monitor</Label>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {MODULES.map((m) => {
            const on = cfg.monitored_modules.includes(m.key);
            return (
              <button key={m.key} onClick={() => toggleModule(m.key)}
                className={`rounded-xl border px-3 py-2 text-sm transition ${on ? "border-fuchsia-500/30 bg-fuchsia-500/10 text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:bg-white/5"}`}
                data-testid={`ea-monitor-${m.key}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 glass rounded-2xl p-5">
        <Label className="text-xs uppercase tracking-wider text-white/50">Delivery</Label>
        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="text-sm text-white">In-app notifications</div>
            <div className="text-xs text-white/45">Show new briefings + tasks in the dashboard.</div>
          </div>
          <Switch checked={cfg.in_app_enabled} onCheckedChange={(v) => save({ in_app_enabled: v })} data-testid="ea-inapp-toggle" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="text-sm text-white">Email digest</div>
            <div className="text-xs text-white/45">Real email available with <span className="text-fuchsia-300">Resend connect</span> (production setup).</div>
          </div>
          <Switch checked={cfg.email_enabled} onCheckedChange={(v) => save({ email_enabled: v })} data-testid="ea-email-toggle" />
        </div>
      </div>
      {saving && <div className="mt-3 text-xs text-white/40">Saving…</div>}
    </div>
  );
}
